import React, { useState, useCallback, useRef, useContext, useEffect } from "react";
import { LANGUAGE_NAMES, POPULAR_LANGUAGES, moreLanguages } from "../../../../constants/languages";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import { InputAdornment, LinearProgress, ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  Box,
  Typography,
  TextField,
  Button,
} from "../../../design-system";
import { colors, shadows } from "../../../design-system/colors";
import { CloudUploadIcon } from "../../../design-system/icons";
import { useFileUpload } from "../hooks/useFileUpload";
import SelectedFilePreview from "./SelectedFilePreview";
import { UPLOAD_DOWNLOAD } from "../constants/homepageText";
import { UPLOAD_AREA_LAYOUT } from "../constants/uploadAreaLayout";
import { WHISPER_ENABLED, importFromUrlEndpoint } from "../../../../components/constants";
import { messageContext } from "../../../../stores/messages/messageContext";
import apiRequest from "../../../../utils/apiClient";
import { limitLink } from "../../../../utils/limitErrorHandler";
import { userContext } from "../../../../stores/users/userContext";
import OnboardingModal from "../../../onboarding/modal/OnboardingModal";
import ModalHeader from "../../../onboarding/modal/ModalHeader";
import ModalContent from "../../../onboarding/modal/ModalContent";
import OnboardingFlow from "../../../onboarding/OnboardingFlow";

const DRAG_DROP_HEIGHT = 380;
const CLOUD_ICON_SIZE = 44;

interface UploadDownloadAreaProps {
  onUploadSuccess?: () => void;
}

/** The language picker's options. `langs` is what the backend receives ([] = auto). */
export const LANGUAGE_CHOICES: { id: string; label: string; langs: string[] }[] = [
  { id: "auto", label: "Auto", langs: [] },
  { id: "en", label: "English", langs: ["en"] },
  { id: "he", label: "Hebrew", langs: ["he"] },
  { id: "yi", label: "Yiddish", langs: ["yi"] },
  { id: "mix", label: "Mixed", langs: ["en", "he", "yi"] },
];
const sameLangs = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i]);
// A language outside the five chips (Russian, say) comes from the "More" list and is sent alone.
const customLanguage = (langs: string[]) =>
  (langs.length === 1 && !LANGUAGE_CHOICES.some((c) => sameLangs(c.langs, langs)) ? langs[0] : "");

const UploadDownloadArea: React.FC<UploadDownloadAreaProps> = ({
  onUploadSuccess,
}) => {
  const messageStore = useContext(messageContext);
  const userStore = useContext(userContext);
  // The flagship shows this uploader to everyone, but the upload API is
  // Authorization-header only — a signed-out visitor used to hand over a video and get
  // back "Invalid token". We sign them in OVER the page instead: navigating away would
  // drop the File object and make them start over, which is the moment you lose them.
  const [authGate, setAuthGate] = useState(false);
  // Mirror LandingPage: only mount the flow after first open, or the Google SDK never
  // injects its button into a hidden (keepMounted) container.
  const [authGateOpened, setAuthGateOpened] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [finishingMsgIndex, setFinishingMsgIndex] = useState(0);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [fileQueue, setFileQueue] = useState<File[]>([]);
  const [showTrackPicker, setShowTrackPicker] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("open") === "1") {
      navigate("/", { replace: true });
      const timer = setTimeout(() => fileInputRef.current?.click(), 80);
      return () => clearTimeout(timer);
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const handler = (e: Event) => setIsButtonHovered((e as CustomEvent).detail);
    window.addEventListener("upload-hover", handler);
    return () => window.removeEventListener("upload-hover", handler);
  }, []);

  useEffect(() => {
    const handler = () => {
      setIsHighlighted(true);
      setTimeout(() => setIsHighlighted(false), 1400);
      fileInputRef.current?.click();
    };
    window.addEventListener("upload-open", handler);
    return () => window.removeEventListener("upload-open", handler);
  }, []);

  const {
    file,
    projectTitle,
    isUploading,
    uploadProgress,
    uploadEtaSeconds,
    uploadFinishing,
    transcriptionEngine,
    audioTrack,
    languages,
    batchCurrent,
    batchTotal,
    handleFileSelect,
    handleFileDrop: _handleFileDrop,
    setTranscriptionEngine,
    setAudioTrack,
    setLanguages,
    uploadFile,
    uploadBatch,
    resetUpload,
    validateFile,
  } = useFileUpload(
    (message) => {
      // Upload-success toast — give the user a direct link to watch it process.
      messageStore.setMessage(message, "success", 7000, {
        text: "Go to My Library",
        url: "/my-library/uploads",
      });
    },
    (error, link) => {
      // Refused for plan time/storage: keep it up long enough to read, with the way out.
      if (link) {
        messageStore.setMessage(error, "error", 20000, link);
        return;
      }
      messageStore.setMessage(error, "error");
    },
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;
      if (files.length > 1) {
        const valid: File[] = [];
        for (let i = 0; i < files.length; i++) {
          const v = validateFile(files[i]);
          if (v.isValid) {
            valid.push(files[i]);
          }
        }
        if (valid.length > 0) {
          setFileQueue(valid);
          handleFileSelect(null);
        }
      } else {
        setFileQueue([]);
        _handleFileDrop(files);
      }
    },
    [_handleFileDrop, handleFileSelect, validateFile],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) {
        e.target.value = "";
        return;
      }
      if (files.length > 1) {
        const valid: File[] = [];
        for (let i = 0; i < files.length; i++) {
          const v = validateFile(files[i]);
          if (v.isValid) {
            valid.push(files[i]);
          }
        }
        if (valid.length > 0) {
          setFileQueue(valid);
          handleFileSelect(null);
        }
      } else {
        setFileQueue([]);
        handleFileSelect(files[0] || null);
      }
      e.target.value = "";
    },
    [handleFileSelect, validateFile],
  );

  const handleClearQueueFile = useCallback((index: number) => {
    setFileQueue((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const runUpload = useCallback(async () => {
    if (fileQueue.length > 0) {
      await uploadBatch(fileQueue, () => {
        setFileQueue([]);
        resetUpload();
        onUploadSuccess?.();
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      });
    } else if (file) {
      const succeeded = await uploadFile();  // no title -> auto-named from the file
      if (succeeded) {
        resetUpload();
        onUploadSuccess?.();
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  }, [file, fileQueue, projectTitle, uploadFile, uploadBatch, resetUpload, onUploadSuccess]);

  const handleGetStarted = useCallback(async () => {
    if (!file && fileQueue.length === 0) return;
    if (!userStore.isUserAuth()) {
      userStore.setPhase("get_email");
      setAuthGateOpened(true);
      setAuthGate(true);
      return;
    }
    await runUpload();
  }, [file, fileQueue, userStore, runUpload]);

  const handleClearFile = useCallback(() => {
    resetUpload();
    setFileQueue([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [resetUpload]);

  // Import from a pasted link. The backend does a fast reachability check FIRST, so the
  // user gets an instant "found it" / "couldn't access it" answer; the heavy download then
  // runs in the background and the item appears in the library like any other upload.
  const handleImportLink = useCallback(async () => {
    const url = linkUrl.trim();
    if (!url || importing) return;
    setImporting(true);
    setImportMsg(null);
    try {
      const res = await apiRequest(importFromUrlEndpoint, {
        method: "POST",
        // The language chosen above rides along, like a file upload's (it used to be dropped).
        body: JSON.stringify({ url, transcription_engine: transcriptionEngine, languages }),
        headers: { "Content-Type": "application/json" },
      });
      const body = await res.json();
      if (res.ok) {
        // Link is valid + importing — send them straight to the library to watch it
        // process, instead of making them wait on the home page.
        setLinkUrl("");
        onUploadSuccess?.();
        navigate("/my-library/uploads");
      } else if (res.status === 403 && body.upgrade) {
        // Same refusal as a file upload (the link's video is longer than the time left).
        // The 403 body says why in `error`; reading only `message` showed "Couldn't import".
        messageStore.setMessage(body.error || "Upload limit exceeded.", "error", 20000, limitLink(body));
      } else {
        setImportMsg({ text: body.message || "Couldn't import that link.", ok: false });
      }
    } catch {
      setImportMsg({ text: "Couldn't reach the server. Please try again.", ok: false });
    } finally {
      setImporting(false);
    }
  }, [linkUrl, importing, transcriptionEngine, languages, onUploadSuccess, messageStore]);

  useEffect(() => {
    if (!uploadFinishing) {
      setFinishingMsgIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setFinishingMsgIndex((i) => (i + 1) % UPLOAD_DOWNLOAD.ETA_FINISHING_MESSAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [uploadFinishing]);

  const isGetStartedEnabled = (fileQueue.length > 0) || Boolean(file);
  const showShadow = isDragging || isHovering || isHighlighted || isButtonHovered;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: { xs: "auto", md: `${DRAG_DROP_HEIGHT}px` },
        border: `2px dashed ${isHighlighted || isButtonHovered || isHovering || isDragging ? "#e61d50" : colors.secondary[500]}`,
        borderRadius: "12px",
        boxShadow: (isButtonHovered || isHovering || isDragging)
          ? "0 0 32px 8px rgba(230,29,80,0.45), 0 0 80px 16px rgba(230,29,80,0.15)"
          : "none",
        transform: (isButtonHovered || isHovering) ? "scale(1.012)" : "scale(1)",
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        animation: isHighlighted ? "uploadPulse 0.5s ease-in-out 3" : "none",
        "@keyframes uploadPulse": {
          "0%":   { boxShadow: "0 0 0 0 rgba(230,29,80,0.7)", transform: "scale(1)" },
          "40%":  { boxShadow: "0 0 0 18px rgba(230,29,80,0)", transform: "scale(1.015)" },
          "100%": { boxShadow: "0 0 0 0 rgba(230,29,80,0)",  transform: "scale(1)" },
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "24px",
        padding: { xs: "24px 16px", md: "48px" },
        cursor: "pointer",
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      {/* The real onboarding modal — same one the landing page uses — so signed-out visitors
          get the designed sign-in, not a second bespoke one. It signs them in over the page:
          navigating away would drop the File they already chose. stopPropagation because the
          dropzone Box opens the file picker on any click and a portalled modal still bubbles
          through the React tree. */}
      <Box onClick={(e) => e.stopPropagation()}>
        <OnboardingModal open={authGate} onClose={() => setAuthGate(false)}>
          {authGateOpened ? (
            <>
              <ModalHeader onClose={() => setAuthGate(false)} />
              <ModalContent>
                {file ? (
                  <Typography sx={{ fontSize: "14px", color: colors.neutral.white, textAlign: "center", opacity: 0.75 }}>
                    “{file.name}” is ready — sign in and it starts straight away.
                  </Typography>
                ) : null}
                <OnboardingFlow
                  onAuthed={() => {
                    setAuthGate(false);
                    setTimeout(() => { runUpload(); }, 0);
                  }}
                />
              </ModalContent>
            </>
          ) : null}
        </OnboardingModal>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*,audio/*,.mp3,.m4a,.wav,.aac,.flac,.ogg,.oga,.opus,.aiff,.aif,.wma,.amr,.3gp,.webm"
        multiple
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />

      {isUploading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
          <CloudUploadIcon size={CLOUD_ICON_SIZE} color={colors.neutral.white} />
          <Typography
            sx={{
              fontSize: { xs: "24px", md: "45px" },
              fontWeight: 700,
              lineHeight: { xs: "32px", md: "62px" },
              color: colors.text[700],
              fontFamily: "Outfit, sans-serif",
              textAlign: "center",
            }}
          >
            {batchTotal > 1
              ? file?.name.replace(/\.[^/.]+$/, "")
              : (projectTitle || file?.name.replace(/\.[^/.]+$/, ""))}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "16px", md: "20px" },
              fontWeight: 700,
              lineHeight: "34px",
              color: colors.neutral.white,
              fontFamily: "Outfit, sans-serif",
              textAlign: "center",
            }}
          >
            <span style={{ color: colors.accent.pink }}>{UPLOAD_DOWNLOAD.UPLOADING}</span>{" your video..."}
          </Typography>
          <Box sx={{ width: "500px", maxWidth: "100%", display: "flex", flexDirection: "column", gap: "9px" }}>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{
                height: 5,
                borderRadius: 6,
                backgroundColor: "rgba(141,153,183,0.15)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#e61d50",
                  borderRadius: 6,
                },
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    border: "1px solid rgba(141,153,183,0.18)",
                    borderRadius: "50%",
                    borderTopColor: "#898d94",
                    animation: "spin 1s linear infinite",
                    flexShrink: 0,
                    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
                  }}
                />
                <Typography sx={{ fontSize: "12px", fontFamily: "Outfit, sans-serif", color: "#898d94", lineHeight: "18px" }}>
                  {uploadFinishing
                    ? UPLOAD_DOWNLOAD.ETA_FINISHING_MESSAGES[finishingMsgIndex]
                    : `${UPLOAD_DOWNLOAD.UPLOADING}… ${uploadProgress}%`}
                </Typography>
              </Box>
              {!uploadFinishing && uploadEtaSeconds !== null && uploadEtaSeconds >= 1 && (
                <Typography sx={{ fontSize: "12px", fontFamily: "Outfit, sans-serif", color: "#a4a9b1", lineHeight: "18px" }}>
                  {uploadEtaSeconds >= 60
                    ? `${Math.floor(uploadEtaSeconds / 60)}m ${uploadEtaSeconds % 60}s`
                    : `${uploadEtaSeconds}s`} {UPLOAD_DOWNLOAD.ETA_LEFT}
                </Typography>
              )}
            </Box>
            {batchTotal > 1 && (
              <Typography sx={{ fontSize: "12px", fontFamily: "Outfit, sans-serif", color: "#a4a9b1", lineHeight: "18px", textAlign: "center" }}>
                {`File ${batchCurrent} of ${batchTotal}`}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        <>
          {/* Box header is now a quiet hint — the bold promise lives in the hero above. */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
            <CloudUploadIcon size={CLOUD_ICON_SIZE} color={colors.neutral.white} />
            <Typography
              sx={{
                fontSize: { xs: "15px", md: "18px" },
                fontWeight: 600,
                color: colors.neutral.white,
                fontFamily: "Outfit, sans-serif",
                textAlign: "center",
              }}
            >
              Drop a file here, upload, or paste a link
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              width: { xs: "100%", md: `${UPLOAD_AREA_LAYOUT.contentWidth}px` },
              maxWidth: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ssemble-style primary row: Upload button sits right next to the paste-link
                field. No "Project Title" step — uploads are auto-named from the file. */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: "10px", md: "12px" },
                width: "100%",
              }}
            >
              {file || fileQueue.length > 0 ? (
                <Button
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); handleGetStarted(); }}
                  disabled={!isGetStartedEnabled}
                  sx={{
                    height: "50px", minHeight: "50px", padding: "0 32px", flexShrink: 0,
                    borderRadius: `${UPLOAD_AREA_LAYOUT.radiusPill}px`,
                    fontSize: "16px", fontWeight: 600, color: "#fff",
                    backgroundColor: "#e61d50", whiteSpace: "nowrap",
                    "&:hover": { backgroundColor: "#c91846" },
                  }}
                >
                  Create Clips →
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  sx={{
                    height: "50px", minHeight: "50px", padding: "0 32px", flexShrink: 0,
                    borderRadius: `${UPLOAD_AREA_LAYOUT.radiusPill}px`,
                    fontSize: "16px", fontWeight: 600, color: "#fff",
                    backgroundColor: "#e61d50", whiteSpace: "nowrap",
                    "&:hover": { backgroundColor: "#c91846" },
                  }}
                >
                  Upload file(s)
                </Button>
              )}

              <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.4)", fontFamily: "Outfit, sans-serif" }}>
                or
              </Typography>

              {/* Paste-a-link field — backend checks reachability BEFORE downloading, so the
                  user gets instant feedback; the heavy download then runs in the background. */}
              <TextField
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); handleImportLink(); }
                }}
                placeholder="Paste a YouTube, Google Drive or Dropbox link"
                inputHeight={50}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "4px" }}>
                      <Button
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleImportLink(); }}
                        disabled={importing || !linkUrl.trim()}
                        sx={{
                          height: "38px", minHeight: "38px", padding: "0 20px",
                          borderRadius: `${UPLOAD_AREA_LAYOUT.radiusPill}px`,
                          fontSize: "14px", fontWeight: 600, color: "#fff",
                          backgroundColor: "#e61d50", whiteSpace: "nowrap", minWidth: "max-content",
                          "&:hover": { backgroundColor: "#c91846" },
                          "&.Mui-disabled": { backgroundColor: "rgba(230,29,80,0.35)", color: "rgba(255,255,255,0.6)" },
                        }}
                      >
                        {importing ? "Fetching…" : "Get Clips"}
                      </Button>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", md: "420px" },
                  maxWidth: "100%",
                  height: "50px",
                }}
              />
            </Box>

            {importMsg && !importMsg.ok && (
              <Typography
                sx={{ fontSize: "12px", fontFamily: "Outfit, sans-serif", textAlign: "center", maxWidth: 380, color: "#ff6b6b" }}
              >
                {importMsg.text}
              </Typography>
            )}

            {fileQueue.length > 1 ? (
              <Box
                sx={{
                  width: { xs: "100%", md: `${UPLOAD_AREA_LAYOUT.urlInputWidth}px` },
                  maxWidth: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                {fileQueue.map((f, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "rgba(255,255,255,0.05)",
                      borderRadius: "6px",
                      padding: "6px 10px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontFamily: "Outfit, sans-serif",
                        color: colors.neutral.white,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        mr: 1,
                      }}
                    >
                      {f.name}
                    </Typography>
                    <Button
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearQueueFile(i);
                      }}
                      sx={{
                        height: "20px",
                        minHeight: "20px",
                        padding: "0 8px",
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "rgba(255,255,255,0.4)",
                        flexShrink: 0,
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
              </Box>
            ) : file ? (
              <SelectedFilePreview file={file} onClear={handleClearFile} />
            ) : null}

            {WHISPER_ENABLED && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", mt: 1 }}>
                <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.5)", fontFamily: "Outfit, sans-serif" }}>
                  Transcription engine
                </Typography>
                <ToggleButtonGroup
                  value={transcriptionEngine}
                  exclusive
                  onChange={(_, val) => { if (val) setTranscriptionEngine(val); }}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      color: "rgba(255,255,255,0.5)",
                      borderColor: "rgba(255,255,255,0.15)",
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "12px",
                      padding: "4px 16px",
                      textTransform: "none",
                    },
                    "& .Mui-selected": {
                      color: "#fff !important",
                      backgroundColor: "rgba(230,29,80,0.25) !important",
                      borderColor: "#e61d50 !important",
                    },
                  }}
                >
                  <ToggleButton value="assemblyai">AssemblyAI</ToggleButton>
                  <ToggleButton value="whisper">Whisper (Local)</ToggleButton>
                  <ToggleButton value="both">Both</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Optional audio-track picker — collapsed by default so it never
                gets in the way. For multi-track recordings (e.g. OBS gameplay
                with separate game/mic/desktop tracks). Default is track 1. */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", mt: 1 }}>
              {/* Which language(s) the speech is in. Auto detects among English / Hebrew /
                  Yiddish and keeps mid-sentence switches; a choice narrows the detection.
                  Same look as the audio-track picker below. */}
              <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.5)", fontFamily: "Outfit, sans-serif" }}>
                Language
              </Typography>
              <ToggleButtonGroup
                value={customLanguage(languages) ? "more" : (LANGUAGE_CHOICES.find((c) => sameLangs(c.langs, languages))?.id || "auto")}
                exclusive
                onChange={(_, id) => { const c = LANGUAGE_CHOICES.find((x) => x.id === id); if (c) setLanguages(c.langs); }}
                size="small"
                sx={{
                  flexWrap: "wrap",
                  "& .MuiToggleButton-root": {
                    color: "rgba(255,255,255,0.5)", borderColor: "rgba(255,255,255,0.15)",
                    fontFamily: "Outfit, sans-serif", fontSize: "12px", padding: "3px 12px", textTransform: "none",
                  },
                  "& .Mui-selected": {
                    color: "#fff !important", backgroundColor: "rgba(230,29,80,0.25) !important",
                    borderColor: "#e61d50 !important",
                  },
                }}
              >
                {LANGUAGE_CHOICES.map((c) => <ToggleButton key={c.id} value={c.id}>{c.label}</ToggleButton>)}
                <ToggleButton value="more" sx={{ padding: "0 !important" }}>
                  {/* Every other language AssemblyAI takes, popular ones first. Picking one
                      lights this chip and sends that language alone. */}
                  <select
                    aria-label="More languages"
                    value={customLanguage(languages)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { if (e.target.value) setLanguages([e.target.value]); }}
                    style={{ background: "transparent", color: "inherit", border: 0, fontFamily: "Outfit, sans-serif", fontSize: "12px", padding: "3px 8px", cursor: "pointer" }}
                  >
                    <option value="">More…</option>
                    <optgroup label="Popular">
                      {POPULAR_LANGUAGES.map((k) => <option key={k} value={k}>{LANGUAGE_NAMES[k]}</option>)}
                    </optgroup>
                    <optgroup label="All languages">
                      {moreLanguages().map((k) => <option key={k} value={k}>{LANGUAGE_NAMES[k]}</option>)}
                    </optgroup>
                  </select>
                </ToggleButton>
              </ToggleButtonGroup>
              {!showTrackPicker ? (
                <Typography
                  onClick={() => setShowTrackPicker(true)}
                  sx={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", fontFamily: "Outfit, sans-serif",
                        cursor: "pointer", "&:hover": { color: "rgba(255,255,255,0.7)" } }}
                >
                  ▸ Multiple audio tracks?
                </Typography>
              ) : (
                <>
                  <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.5)", fontFamily: "Outfit, sans-serif" }}>
                    Audio track to transcribe
                  </Typography>
                  <ToggleButtonGroup
                    value={audioTrack}
                    exclusive
                    onChange={(_, val) => { if (val) setAudioTrack(val); }}
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root": {
                        color: "rgba(255,255,255,0.5)", borderColor: "rgba(255,255,255,0.15)",
                        fontFamily: "Outfit, sans-serif", fontSize: "12px", padding: "3px 12px", textTransform: "none",
                      },
                      "& .Mui-selected": {
                        color: "#fff !important", backgroundColor: "rgba(230,29,80,0.25) !important",
                        borderColor: "#e61d50 !important",
                      },
                    }}
                  >
                    <ToggleButton value={1}>1</ToggleButton>
                    <ToggleButton value={2}>2</ToggleButton>
                    <ToggleButton value={3}>3</ToggleButton>
                    <ToggleButton value={4}>4</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.3)", fontFamily: "Outfit, sans-serif", textAlign: "center", maxWidth: 260 }}>
                    Most videos use track 1. Pick another only for multi-track recordings (e.g. OBS).
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default observer(UploadDownloadArea);
